import { useState } from 'react';

import { Button } from '@mui/material';
import { omit } from '@npi/buddy-utils';
import { Hierarchy } from '@prisma/client';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { navigate, routes } from '@redwoodjs/router';
import { MetaTags, useMutation } from '@redwoodjs/web';

import { Spacer } from 'src/components';
import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import useGetLedgerAccounts from 'src/features/finance/hooks/useGetLedgerAccounts';
import { CREATE_CREDIT_NOTE } from 'src/graphql/mutations/createCreditNote.mutation';
import { useCurrency } from 'src/hooks/useCurrency';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import AddCreditNoteValidationSchema from 'src/validation/CreditNote/AddCreditNote.validation';

import {
    CreditNoteDetails,
    CreditNoteItem,
    CreditNoteReason,
    CreditNoteTotals,
} from './components';
import { MerchantOptionTypes } from './components/CreditNoteDetails';

const INITIAL_CREDIT_NOTE_ITEM = {
    id: '',
    ledgerAccount: '',
    description: '',
    quantity: 0,
    netAmount: 0,
    totalNet: 0,
    totalVat: 0,
    lineTotal: 0,
};

export const createCreditNoteItem = () => {
    return {
        ...INITIAL_CREDIT_NOTE_ITEM,
        id: uuidv4(),
    };
};

const AddCreditNotePage = () => {
    const navigateTo = useNavigate();
    const [selectedInvoiceTo, setSelectedInvoiceTo] = useState<MerchantOptionTypes | undefined>(
        undefined
    );

    const { ledgerAccounts, fetchingLedgerAccounts } = useGetLedgerAccounts();

    const { currencySymbol } = useCurrency();

    const [addCreditNote, __] = useMutation(CREATE_CREDIT_NOTE, {
        onCompleted: (data) => {
            const creditNoteNumber = data?.createCreditNote?.sageCreditNoteNumber
                ? `${data?.createCreditNote?.sageCreditNoteNumber} `
                : '';
            showSuccessToast(`Credit Note ${creditNoteNumber}created successfully`);
            navigate(routes.credits());
        },
        onError: (data) => {
            showErrorToast(data?.message ?? 'Error Creating Credit Note');
        },
    });

    const submitCreditNote = async (input) => {
        const creditNoteInput = { ...input };
        delete creditNoteInput.isInvoiceSelected;
        delete creditNoteInput.currencySymbol;

        const { totalCreditAmount, totalCreditNetAmount, totalCreditVatAmount } = creditNoteInput;

        await addCreditNote({
            variables: {
                input: {
                    ...creditNoteInput,
                    creditNoteItems: creditNoteInput.creditNoteItems.map((item) => {
                        const { lineTotal, netAmount, totalNet, totalVat } = item;
                        const itemWithoutId = omit(item, ['id']);

                        return {
                            ...itemWithoutId,
                            lineTotal,
                            netAmount,
                            totalNet,
                            totalVat,
                        };
                    }),
                    totalCreditAmount,
                    totalCreditNetAmount,
                    totalCreditVatAmount,
                    creditNoteDate: new Date(),
                    invoice: creditNoteInput?.invoice?.value,
                    invoiceTo: selectedInvoiceTo.value,
                },
            },
        });
    };

    return (
        <PageContent heading="Add New Credit">
            <MetaTags title="Add New Credit" description="Add New Credit" />
            {fetchingLedgerAccounts && <Loader />}

            <Formik
                initialValues={{
                    invoiceTo: null,
                    invoiceToType: Hierarchy.MERCHANT,
                    totalCreditNetAmount: 0,
                    totalCreditVatAmount: 0,
                    totalCreditAmount: 0,
                    creditNoteItems: [],
                    invoice: null,
                    reason: '',
                    isInvoiceSelected: false,
                    currencySymbol,
                }}
                enableReinitialize
                onSubmit={submitCreditNote}
                validationSchema={AddCreditNoteValidationSchema}
            >
                {({ handleSubmit, isSubmitting, validateForm }) => {
                    return (
                        <Form>
                            <CreditNoteDetails setSelectedInvoiceTo={setSelectedInvoiceTo} />
                            <Spacer padding={1.5} />
                            <CreditNoteTotals />
                            <Spacer padding={1.5} />
                            <CreditNoteItem ledgerAccounts={ledgerAccounts} />
                            <Spacer padding={1.5} />
                            <CreditNoteReason />
                            <Spacer padding={1.5} />
                            <Flex gap="24px" justifyContent="flex-end">
                                <Button
                                    onClick={() => {
                                        return navigateTo(-1);
                                    }}
                                    variant="main"
                                    sx={{ bgcolor: '#686867' }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={async (event) => {
                                        event.preventDefault();

                                        const errors = await validateForm();

                                        if (errors && Object.keys(errors).length > 0) {
                                            showErrorToast(
                                                (errors?.creditNoteItems as string) ??
                                                    'Please fill all required fields'
                                            );
                                            return;
                                        }
                                        handleSubmit();
                                    }}
                                    variant="main"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Submitting Credit Note...' : 'Save'}
                                </Button>
                            </Flex>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};
export default AddCreditNotePage;
